import React, { useMemo } from 'react';
import { useFormikContext } from 'formik';

import { buildHierarchicalOptions } from '~/components/core/Editor/MenuBar/suggestion/suggestion';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import EmailGenericTemplatesEditor from '~/components/GenericTemplates/GenericTemplatesConfiguration/GenericTemplateBuilder/EmailGenericTemplatesEditor';
import NoteGenericTemplatesEditor from '~/components/GenericTemplates/GenericTemplatesConfiguration/GenericTemplateBuilder/NoteGenericTemplateEditor';
import NotificationGenericTemplatesEditor from '~/components/GenericTemplates/GenericTemplatesConfiguration/GenericTemplateBuilder/NotificationGenericTemplateEditor';
import SmsGenericTemplatesEditorFormik from '~/components/GenericTemplates/GenericTemplatesConfiguration/GenericTemplateBuilder/SmsGenericTemplatesEditorFormik';
import { buildGetTemplateTokensQueryParams } from '~/components/GenericTemplates/GenericTemplatesConfiguration/GenericTemplateBuilder/utils';
import type { TemplateTypeKey } from '~/components/GenericTemplates/types';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import type { SuggestionToken } from '~/components/SystemConfiguration/SystenConfigurationTypes';
import useDataFetcher from '~/components/useDataFetcher';

const EDITOR_COMPONENT_BY_TEMPLATE_TYPE = {
  email: {
    component: EmailGenericTemplatesEditor,
  },
  moi_email: {
    component: EmailGenericTemplatesEditor,
  },
  note: {
    component: NoteGenericTemplatesEditor,
  },
  sms: {
    component: SmsGenericTemplatesEditorFormik,
  },
  notification: {
    component: NotificationGenericTemplatesEditor,
  },
  // TODO: Enable this in NGTPA-17181
  document: {
    component: null,
  },
} as const;

interface TemplateBuilderEditorProps {
  templateType: TemplateTypeKey;
}

// these are only partial values
interface FormValues {
  lobs: string[];
  is_claim_level: boolean;
  is_all_suborgs: boolean;
  sub_organization_ids: number[];
}

interface TemplateTokensResponse {
  tokens: SuggestionToken[];
}

export const TemplateBuilderEditor: React.FC<TemplateBuilderEditorProps> = ({ templateType }) => {
  const { isSubmitting, values } = useFormikContext<FormValues>();
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { organization } = useSysconfig();

  const { data, isLoading } = useDataFetcher<TemplateTokensResponse>(
    `/api/v1/generic_templates/organization/${organization.id}/template/tokens?${buildGetTemplateTokensQueryParams({
      lobs: values.lobs,
      is_claim_level: values.is_claim_level,
      // MOI tokens are feature-scoped on the backend; without this the MOI Email picker gets only the generic token set
      feature_specific: templateType === 'moi_email' ? 'moi_email' : undefined,
      sub_organization_ids: values.is_all_suborgs ? undefined : values.sub_organization_ids,
    })}`,
    {}
  );

  const ComponentFormik = EDITOR_COMPONENT_BY_TEMPLATE_TYPE[templateType].component;

  // Build hierarchical options structure from tokens with token_group
  // This structure is used directly for menus (hierarchical) and flattened for autocomplete
  const suggestions = useMemo(() => {
    if (!data?.tokens) {
      return [];
    }
    return buildHierarchicalOptions(data.tokens);
  }, [data?.tokens]);

  if (!ComponentFormik) {
    return null;
  }

  // Need to unmount and remount editor component so suggestions will get updated
  if (!data || isLoading) {
    return <SkeletonTable rowsCount={5} columnsCount={1} maxHeight="100px" />;
  }
  return <ComponentFormik disabled={isSubmitting} suggestions={suggestions} />;
};
